/**
 * Board Member Dialog
 * Modal for adding/editing board members with permissions
 */
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Loader2 } from "lucide-react";
import { BOARD_POSITIONS, BOARD_PERMISSIONS } from "./constants";

export const BoardMemberDialog = ({
  open,
  onOpenChange,
  boardForm,
  setBoardForm,
  editMode,
  saving,
  onSave
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editMode ? "Edit Board Member" : "Add Board Member"}</DialogTitle>
          <DialogDescription>Add members of your board of directors</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>First Name *</Label>
              <Input 
                value={boardForm.first_name} 
                onChange={(e) => setBoardForm({ ...boardForm, first_name: e.target.value })} 
                className="mt-1" 
              />
            </div>
            <div>
              <Label>Last Name *</Label>
              <Input 
                value={boardForm.last_name} 
                onChange={(e) => setBoardForm({ ...boardForm, last_name: e.target.value })} 
                className="mt-1" 
              />
            </div>
          </div>
          <div>
            <Label>Email</Label>
            <Input 
              type="email" 
              value={boardForm.email} 
              onChange={(e) => setBoardForm({ ...boardForm, email: e.target.value })} 
              className="mt-1" 
            />
          </div>
          <div>
            <Label>Phone</Label>
            <Input 
              type="tel" 
              value={boardForm.phone} 
              onChange={(e) => setBoardForm({ ...boardForm, phone: e.target.value })} 
              className="mt-1" 
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Position</Label>
              <Select 
                value={boardForm.position} 
                onValueChange={(value) => setBoardForm({ ...boardForm, position: value })}
              >
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BOARD_POSITIONS.map(pos => (
                    <SelectItem key={pos.value} value={pos.value}>{pos.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Custom Title</Label>
              <Input 
                value={boardForm.title} 
                onChange={(e) => setBoardForm({ ...boardForm, title: e.target.value })} 
                placeholder="e.g., Founding President" 
                className="mt-1" 
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Term Start</Label>
              <Input 
                type="date" 
                value={boardForm.term_start} 
                onChange={(e) => setBoardForm({ ...boardForm, term_start: e.target.value })} 
                className="mt-1" 
              />
            </div>
            <div>
              <Label>Term End</Label>
              <Input 
                type="date" 
                value={boardForm.term_end} 
                onChange={(e) => setBoardForm({ ...boardForm, term_end: e.target.value })} 
                className="mt-1" 
              />
            </div>
          </div>
          <div>
            <Label>Bio</Label>
            <Textarea 
              value={boardForm.bio} 
              onChange={(e) => setBoardForm({ ...boardForm, bio: e.target.value })} 
              placeholder="Brief biography..." 
              rows={3} 
              className="mt-1" 
            />
          </div>
          <div>
            <Label>LinkedIn URL</Label>
            <Input 
              value={boardForm.linkedin_url} 
              onChange={(e) => setBoardForm({ ...boardForm, linkedin_url: e.target.value })} 
              placeholder="https://linkedin.com/in/..." 
              className="mt-1" 
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Checkbox 
              checked={boardForm.show_on_public_page} 
              onCheckedChange={(checked) => setBoardForm({ ...boardForm, show_on_public_page: checked })} 
            />
            <Label>Show on public organization page</Label>
          </div>

          {/* System Access & Permissions */}
          <div className="border-t border-slate-200 pt-4 mt-4">
            <div className="flex items-center gap-2 mb-4">
              <Checkbox 
                checked={boardForm.has_system_access} 
                onCheckedChange={(checked) => setBoardForm({ ...boardForm, has_system_access: checked })} 
                id="has_system_access"
              />
              <Label htmlFor="has_system_access" className="font-semibold text-slate-900">Grant System Access</Label>
            </div>
            
            {boardForm.has_system_access && (
              <div className="space-y-4 pl-6 border-l-2 border-purple-200 ml-2">
                <p className="text-sm text-slate-500">Select which permissions this board member should have:</p>
                
                <div className="space-y-3">
                  {BOARD_PERMISSIONS.map((perm) => (
                    <div key={perm.key} className="flex items-start gap-3">
                      <Checkbox 
                        checked={boardForm.permissions[perm.key]} 
                        onCheckedChange={(checked) => {
                          if (perm.key === "full_admin" && checked) {
                            const allPermissions = {};
                            BOARD_PERMISSIONS.forEach(p => allPermissions[p.key] = true);
                            setBoardForm({ ...boardForm, permissions: allPermissions });
                          } else {
                            setBoardForm({ 
                              ...boardForm, 
                              permissions: { 
                                ...boardForm.permissions, 
                                [perm.key]: checked,
                                ...(perm.key !== "full_admin" && !checked ? { full_admin: false } : {})
                              } 
                            });
                          }
                        }}
                        id={`perm_${perm.key}`}
                        disabled={perm.key !== "full_admin" && boardForm.permissions.full_admin}
                      />
                      <div>
                        <Label 
                          htmlFor={`perm_${perm.key}`} 
                          className={`text-sm font-medium ${perm.key === "full_admin" ? "text-purple-700" : "text-slate-700"}`}
                        >
                          {perm.label}
                        </Label>
                        <p className="text-xs text-slate-500">{perm.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                {boardForm.email && !editMode && (
                  <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        checked={boardForm.send_invite} 
                        onCheckedChange={(checked) => setBoardForm({ ...boardForm, send_invite: checked })}
                        id="send_invite"
                      />
                      <Label htmlFor="send_invite" className="text-sm text-purple-800">
                        Send invitation email to set up their account
                      </Label>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={saving} className="bg-slate-900">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {editMode ? "Update" : "Add Board Member"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BoardMemberDialog;
